import { InboxOutlined } from "@ant-design/icons"
import type { UploadFile } from "antd"
import { App, Form, Modal, Progress, Radio, Select, Typography, Upload } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { LeadPickerSelect } from "../../../components/Shared/LeadPickerSelect.tsx"
import { ProjectPickerSelect } from "../../../components/Shared/ProjectPickerSelect.tsx"
import { useDocumentTypeOptions } from "../../Files/hooks/useDocumentTypeOptions.ts"
import { createProjectDocument } from "../../Project/api/project-documents.services.ts"

const { Text } = Typography

type UploadDestination = "company" | "project" | "lead"

interface GlobalUploadModalProps {
  onClose: () => void
  onUploaded: () => void
}

/** Global Upload (EAD-615 Phase B) — the Global Files "+ Upload" modal.
 * Defaults to Company/General (the GC opened the company-wide workspace, so
 * that should be the easiest destination to land on), and lets the caller
 * switch to an authorized Project or Lead instead. Mirrors
 * `UploadDocumentModal`'s own sequential-upload/progress/partial-success
 * shape rather than duplicating it wholesale — the one real difference is
 * the destination picker up front, which resolves to the same `lead=`
 * field `createProjectDocument` already accepts (omitted entirely for a
 * Company/General upload). */
export function GlobalUploadModal({ onClose, onUploaded }: GlobalUploadModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [destination, setDestination] = useState<UploadDestination>("company")
  const [projectId, setProjectId] = useState<string | undefined>()
  const [leadId, setLeadId] = useState<string | undefined>()
  const [documentType, setDocumentType] = useState<string | undefined>()
  const { options: documentTypes, loadFailed: documentTypesFailed } = useDocumentTypeOptions()
  const [files, setFiles] = useState<UploadFile[]>([])
  const [uploaded, setUploaded] = useState<number | null>(null)

  const destinationId = destination === "project" ? projectId : destination === "lead" ? leadId : undefined
  const canSubmit =
    files.length > 0 && (destination === "company" || Boolean(destinationId))

  const handleSubmit = async () => {
    if (!canSubmit) return

    setUploaded(0)
    let done = 0
    let refusal: string | undefined
    for (const [index, entry] of files.entries()) {
      const selected = entry.originFileObj
      if (!selected) continue

      const response = await createProjectDocument({
        ...(destinationId ? { lead: destinationId } : {}),
        file_name: entry.name,
        file: selected,
        ...(documentType ? { document_type: documentType } : {})
      })
      if (response.ok) done += 1
      else refusal ??= response.data?.details?.message
      setUploaded(index + 1)
    }
    setUploaded(null)

    if (!done) {
      message.error(refusal || t("globalFiles.upload.failed", "Upload failed."))
      return
    }
    if (done < files.length) {
      message.warning(
        t("globalFiles.upload.partial", "{{done}} of {{total}} files uploaded.", {
          done,
          total: files.length
        })
      )
    } else {
      message.success(
        t("globalFiles.upload.succeeded", "{{count}} file(s) uploaded.", { count: done })
      )
    }

    onUploaded()
    onClose()
  }

  const isSaving = uploaded !== null

  return (
    <Modal
      open
      title={t("globalFiles.upload.title", "Upload Files")}
      okText={t("globalFiles.upload.submit", "Upload")}
      onOk={() => void handleSubmit()}
      onCancel={onClose}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: !canSubmit }}
      cancelButtonProps={{ disabled: isSaving }}
    >
      <Form layout="vertical" requiredMark={false}>
        <Form.Item
          label={t("globalFiles.upload.file", "Files")}
          required
          validateStatus={files.length ? undefined : "warning"}
        >
          <Upload.Dragger
            multiple
            beforeUpload={() => false}
            fileList={files}
            disabled={isSaving}
            onChange={({ fileList }) => setFiles(fileList)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <Text>{t("globalFiles.upload.dragHint", "Drag files here or browse")}</Text>
          </Upload.Dragger>
        </Form.Item>

        <Form.Item label={t("globalFiles.upload.destinationLabel", "Where should these files live?")}>
          <Radio.Group
            value={destination}
            disabled={isSaving}
            onChange={(event) => setDestination(event.target.value as UploadDestination)}
          >
            <Radio value="company">
              {t("globalFiles.upload.destinationCompany", "Company / General")}
            </Radio>
            <Radio value="project">{t("globalFiles.upload.destinationProject", "Project")}</Radio>
            <Radio value="lead">{t("globalFiles.upload.destinationLead", "Lead")}</Radio>
          </Radio.Group>
        </Form.Item>

        {destination === "project" && (
          <Form.Item
            label={t("globalFiles.upload.projectLabel", "Project")}
            required
            validateStatus={projectId ? undefined : "warning"}
          >
            <ProjectPickerSelect
              value={projectId}
              onChange={(value) => setProjectId(value as string)}
              placeholder={t("globalFiles.upload.projectPlaceholder", "Search projects...")}
              disabled={isSaving}
            />
          </Form.Item>
        )}

        {destination === "lead" && (
          <Form.Item
            label={t("globalFiles.upload.leadLabel", "Lead")}
            required
            validateStatus={leadId ? undefined : "warning"}
          >
            <LeadPickerSelect
              value={leadId}
              onChange={(value) => setLeadId(value as string)}
              placeholder={t("globalFiles.upload.leadPlaceholder", "Search leads...")}
              disabled={isSaving}
            />
          </Form.Item>
        )}

        <Form.Item label={t("globalFiles.upload.documentType", "Document Type")}>
          <Select
            allowClear
            disabled={isSaving}
            value={documentType}
            onChange={setDocumentType}
            placeholder={t("globalFiles.upload.documentTypePlaceholder", "Select document type")}
            notFoundContent={
              documentTypesFailed ? t("files.documentType.loadFailed") : undefined
            }
            options={documentTypes}
          />
        </Form.Item>

        {isSaving && (
          <Progress
            percent={Math.round(((uploaded ?? 0) / files.length) * 100)}
            status="active"
          />
        )}
      </Form>
    </Modal>
  )
}
